import React, { useEffect, useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { useWebSocketMessages } from '../hooks/useWebSocket';
import { useAgentExecutions } from '../hooks/useAgentExecutions';
import StaticPipelineNode from './nodes/StaticPipelineNode';
import SubAgentEdge from './edges/SubAgentEdge';

interface PipelineVisualizationProps {
  workflowId: number;
  pipelineId: number;
  pipelineSteps: any[];
  workflowStatus?: string;
}

const nodeTypes = {
  processor: StaticPipelineNode,
};

const edgeTypes = {
  subagent: SubAgentEdge,
};

// Auto-layout using dagre
const getLayoutedElements = (nodes: Node[], edges: Edge[], direction = 'LR') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, ranksep: 80, nodesep: 50 });

  // Separate main pipeline nodes from sub-agent nodes
  const mainNodes = nodes.filter((n) => !n.data.isSubAgent);
  const subAgentNodes = nodes.filter((n) => n.data.isSubAgent);

  // Layout main pipeline only (using fixed node dimensions from StaticPipelineNode)
  mainNodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 180, height: 60 });
  });

  edges.forEach((edge) => {
    // Only add edges between main nodes to dagre
    const sourceIsMain = mainNodes.some((n) => n.id === edge.source);
    const targetIsMain = mainNodes.some((n) => n.id === edge.target);
    if (sourceIsMain && targetIsMain) {
      dagreGraph.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(dagreGraph);

  // Layout main pipeline nodes
  const layoutedMainNodes = mainNodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 90, // Half of width (180 / 2)
        y: nodeWithPosition.y - 30, // Half of height (60 / 2)
      },
    };
  });

  // Manually position sub-agent chains
  // Group sub-agent nodes by their orchestrator parent
  const subAgentChains: Record<string, Node[]> = {};
  subAgentNodes.forEach((node) => {
    const orchestratorId = node.data.orchestratorId;
    if (!subAgentChains[orchestratorId]) {
      subAgentChains[orchestratorId] = [];
    }
    subAgentChains[orchestratorId].push(node);
  });

  const layoutedSubAgentNodes: Node[] = [];
  Object.entries(subAgentChains).forEach(([orchestratorId, chainNodes]) => {
    // Find orchestrator position (could be in main nodes OR already-processed sub-agents for nested orchestrators)
    const orchestratorNode = 
      layoutedMainNodes.find((n) => n.id === orchestratorId) ||
      layoutedSubAgentNodes.find((n) => n.id === orchestratorId);
    if (!orchestratorNode) {
      console.warn(`[PipelineViz] Could not find orchestrator node: ${orchestratorId}`);
      return;
    }

    // Group nodes by sub-agent name (chain)
    const chainsByName: Record<string, Node[]> = {};
    chainNodes.forEach((node) => {
      const chainName = node.data.subAgentName || 'default';
      if (!chainsByName[chainName]) {
        chainsByName[chainName] = [];
      }
      chainsByName[chainName].push(node);
    });

    // Stack chains vertically below orchestrator
    let currentY = orchestratorNode.position.y + 100; // Start below orchestrator (closer to main pipeline)
    Object.values(chainsByName).forEach((chain) => {
      // Build dependency graph to order nodes correctly
      const orderedChain: Node[] = [];
      const visited = new Set<string>();
      
      // Find the first node (connected to orchestrator)
      const firstNode = chain.find((node) => 
        edges.some((e) => e.source === orchestratorId && e.target === node.id)
      );
      
      if (firstNode) {
        orderedChain.push(firstNode);
        visited.add(firstNode.id);
        
        // Follow the chain
        while (orderedChain.length < chain.length) {
          const currentNode = orderedChain[orderedChain.length - 1];
          const currentId = currentNode.id;
          
          const nextEdge = edges.find((e) => 
            e.source === currentId && 
            chain.some((n) => n.id === e.target) &&
            !visited.has(e.target)
          );
          
          if (nextEdge) {
            const nextNode = chain.find((n) => n.id === nextEdge.target);
            if (nextNode) {
              orderedChain.push(nextNode);
              visited.add(nextNode.id);
            } else {
              break;
            }
          } else {
            break;
          }
        }
        
        // Add any remaining nodes (shouldn't happen in well-formed chains)
        chain.forEach((node) => {
          if (!visited.has(node.id)) {
            orderedChain.push(node);
          }
        });
      } else {
        // Fallback: use all nodes in order
        orderedChain.push(...chain);
      }

      // Position nodes in chain horizontally (left-to-right)
      // Start at medium distance from orchestrator for balanced layout
      let currentX = orchestratorNode.position.x + 150;
      orderedChain.forEach((node, idx) => {
        layoutedSubAgentNodes.push({
          ...node,
          position: {
            x: currentX + (idx * 220), // Space nodes horizontally
            y: currentY,
          },
        });
      });

      currentY += 100; // Stack next chain below (keep same spacing between chains)
    });
  });

  return { nodes: [...layoutedMainNodes, ...layoutedSubAgentNodes], edges };
};

const PipelineVisualization: React.FC<PipelineVisualizationProps> = ({
  workflowId,
  pipelineId,
  pipelineSteps,
  workflowStatus,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Check if workflow is paused
  const isWorkflowPaused = workflowStatus === 'paused' || 
                           workflowStatus === 'waiting_approval' ||
                           workflowStatus === 'WAITING_APPROVAL';
  
  // Fetch current execution statuses to initialize with correct states
  const { agentExecutions } = useAgentExecutions(workflowId.toString());

  // Convert pipeline steps to nodes and edges
  const initializePipeline = useCallback(() => {
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];
    const nodeMap = new Map<string, any>();

    // Create nodes for all main pipeline steps
    pipelineSteps.forEach((step) => {
      const stepId = step.step_id;
      
      const node: Node = {
        id: stepId,
        type: 'processor',
        data: {
          label: step.name || step.processor_name,
          processorType: step.processor_name,
          status: 'pending', // Always start as pending, updateNodeStatus will change it
          isSubAgent: false,
          isWorkflowPaused,
        },
        position: { x: 0, y: 0 }, // Will be set by dagre
      };

      newNodes.push(node);
      nodeMap.set(stepId, step);
      
      // Recursive helper to process sub-agents at any depth
      const processSubAgents = (parentStepId: string, subAgents: any[]) => {
        console.log(`[PipelineViz] Processing sub-agents for parent: ${parentStepId}`, subAgents);
        subAgents.forEach((subAgent: any) => {
          console.log(`[PipelineViz] Sub-agent object:`, subAgent);
          if (subAgent.steps && subAgent.steps.length > 0) {
            console.log(`[PipelineViz] Sub-agent "${subAgent.name}" has ${subAgent.steps.length} steps`);
            // Create node for each step in the sub-agent chain
            subAgent.steps.forEach((subStep: any) => {
              const subStepId = subStep.step_id || `${parentStepId}.${subAgent.name}.${subStep.processor_name}`;
              console.log(`[PipelineViz] Creating node for sub-step: ${subStepId} (processor: ${subStep.processor_name})`);
              
              const subNode: Node = {
                id: subStepId,
                type: 'processor',
                data: {
                  label: subStep.name || subStep.processor_name,
                  processorType: subStep.processor_name,
                  status: 'pending',
                  isSubAgent: true,
                  orchestratorId: parentStepId,
                  subAgentName: subAgent.name,
                  isWorkflowPaused,
                },
                position: { x: 0, y: 0 },
              };
              
              newNodes.push(subNode);
              nodeMap.set(subStepId, subStep);

              // RECURSIVE: If this sub-step is itself an orchestrator with sub-agents
              if (subStep.processor_name === 'orchestrator' && subStep.config?.sub_agents) {
                console.log(`[PipelineViz] *** NESTED ORCHESTRATOR DETECTED: ${subStepId} with ${subStep.config.sub_agents.length} sub-agents`);
                processSubAgents(subStepId, subStep.config.sub_agents);
              }
            });
          } else {
            console.log(`[PipelineViz] WARNING: Sub-agent "${subAgent.name}" has NO steps or empty steps array!`, subAgent);
          }
        });
      };
      
      // If this is an orchestrator, check for sub-agents in its config
      if (step.processor_name === 'orchestrator' && step.config?.sub_agents) {
        processSubAgents(stepId, step.config.sub_agents);
      }
    });

    // Create edges based on execution order and data connections
    pipelineSteps.forEach((step) => {
      const stepId = step.step_id;

      // Execution order edge (for non-input steps)
      if (step.order > 0) {
        const prevStep = pipelineSteps.find((s) => s.order === step.order - 1);
        if (prevStep) {
          newEdges.push({
            id: `exec-${prevStep.step_id}-${stepId}`,
            source: prevStep.step_id,
            sourceHandle: 'right',
            target: stepId,
            targetHandle: 'left',
            type: 'smoothstep',
            style: { stroke: '#94a3b8', strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: '#94a3b8',
            },
          });
        }
      }

      // Recursive helper to create sub-agent edges at any depth
      const createSubAgentEdges = (parentStepId: string, subAgents: any[]) => {
        subAgents.forEach((subAgent: any) => {
          if (subAgent.steps && subAgent.steps.length > 0) {
            // Create edge to first step in sub-agent chain
            const firstSubStep = subAgent.steps[0];
            const firstSubStepId = firstSubStep.step_id || `${parentStepId}.${subAgent.name}.${firstSubStep.processor_name}`;
            
            newEdges.push({
              id: `subagent-${parentStepId}-${firstSubStepId}`,
              source: parentStepId,
              sourceHandle: 'bottom',
              target: firstSubStepId,
              targetHandle: 'left',
              type: 'subagent',
              style: { stroke: '#a855f7', strokeWidth: 2, strokeDasharray: '5,5' },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#a855f7',
              },
            });
            
            // Create edges between sub-agent steps
            for (let i = 1; i < subAgent.steps.length; i++) {
              const prevSubStep = subAgent.steps[i - 1];
              const currSubStep = subAgent.steps[i];
              const prevId = prevSubStep.step_id || `${parentStepId}.${subAgent.name}.${prevSubStep.processor_name}`;
              const currId = currSubStep.step_id || `${parentStepId}.${subAgent.name}.${currSubStep.processor_name}`;
              
              newEdges.push({
                id: `subagent-chain-${prevId}-${currId}`,
                source: prevId,
                sourceHandle: 'right',
                target: currId,
                targetHandle: 'left',
                type: 'smoothstep',
                style: { stroke: '#a855f7', strokeWidth: 2, strokeDasharray: '5,5' },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: '#a855f7',
                },
              });
            }

            // RECURSIVE: If any sub-step is an orchestrator, process its sub-agents
            subAgent.steps.forEach((subStep: any) => {
              if (subStep.processor_name === 'orchestrator' && subStep.config?.sub_agents) {
                const subStepId = subStep.step_id || `${parentStepId}.${subAgent.name}.${subStep.processor_name}`;
                createSubAgentEdges(subStepId, subStep.config.sub_agents);
              }
            });
          }
        });
      };

      // Create sub-agent edges (orchestrator to sub-agent steps)
      if (step.processor_name === 'orchestrator' && step.config?.sub_agents) {
        createSubAgentEdges(stepId, step.config.sub_agents);
      }

      // Data dependency edges
      if (step.input_artifact_keys) {
        Object.entries(step.input_artifact_keys).forEach(([key, value]: [string, any]) => {
          if (value?.from_step && value.from_step !== 'input') {
            const sourceId = value.from_step;
            if (nodeMap.has(sourceId)) {
              newEdges.push({
                id: `data-${sourceId}-${key}-${stepId}`,
                source: sourceId,
                sourceHandle: 'right',
                target: stepId,
                targetHandle: 'left',
                type: 'smoothstep',
                animated: false,
                style: { stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '3,3' },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: '#cbd5e1',
                },
              });
            }
          }
        });
      }
    });

    // Apply auto-layout
    console.log(`[PipelineViz] Before layout: ${newNodes.length} nodes, ${newEdges.length} edges`);
    console.log('[PipelineViz] Node IDs:', newNodes.map(n => n.id));
    
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      newNodes,
      newEdges
    );

    console.log(`[PipelineViz] After layout: ${layoutedNodes.length} nodes, ${layoutedEdges.length} edges`);
    
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [pipelineSteps, setNodes, setEdges, isWorkflowPaused]); // Include isWorkflowPaused for node data

  // Initialize pipeline on mount
  useEffect(() => {
    initializePipeline();
  }, [initializePipeline]);

  // Update node status based on execution updates
  const updateNodeStatus = useCallback((stepId: string, status: string) => {
    // Strip execution number from end: "orch.sub.proc.2" → "orch.sub.proc"
    const staticStepId = stepId.replace(/\.\d+$/, '');
    
    const isRunningStatus = status.toLowerCase() === 'in_progress' || 
                           status.toLowerCase() === 'running' ||
                           status.toLowerCase() === 'calling_sub_agent';

    setNodes((nds) =>
      nds.map((node) => {
        // Strip .# from node.id for comparison (sub-agent placeholder)
        const nodeIdWithoutPlaceholder = node.id.replace(/\.#$/, '');
        
        if (nodeIdWithoutPlaceholder === staticStepId || 
            node.id === staticStepId || 
            node.id === stepId) {
          return {
            ...node,
            data: {
              ...node.data,
              status,
              isWorkflowPaused, // Update paused state
            },
          };
        }
        // Also check if this is a sub-agent step that matches the pattern
        if (node.data.isSubAgent && staticStepId.startsWith(nodeIdWithoutPlaceholder)) {
          return {
            ...node,
            data: {
              ...node.data,
              status,
              isWorkflowPaused, // Update paused state
            },
          };
        }
        // If a new step is starting, reset other running steps to pending
        if (isRunningStatus && 
            (node.data.status === 'in_progress' || 
             node.data.status === 'running' || 
             node.data.status === 'calling_sub_agent')) {
          return {
            ...node,
            data: {
              ...node.data,
              status: 'pending',
              isWorkflowPaused, // Update paused state
            },
          };
        }
        return node;
      })
    );
  }, [setNodes, isWorkflowPaused]);
  
  // Apply current execution statuses to nodes after initialization
  useEffect(() => {
    if (agentExecutions && agentExecutions.length > 0 && nodes.length > 0) {
      agentExecutions.forEach((exec) => {
        if (exec.step_id && exec.status) {
          updateNodeStatus(exec.step_id, exec.status);
        }
      });
    }
  }, [agentExecutions, nodes.length, updateNodeStatus]); // Only run when executions or node count changes
  
  // Update all nodes when workflow paused state changes
  useEffect(() => {
    if (nodes.length > 0) {
      // Check if any node needs updating
      const needsUpdate = nodes.some(node => node.data.isWorkflowPaused !== isWorkflowPaused);
      
      if (needsUpdate) {
        setNodes((nds) =>
          nds.map((node) => ({
            ...node,
            data: {
              ...node.data,
              isWorkflowPaused,
            },
          }))
        );
      }
    }
  }, [isWorkflowPaused, nodes, setNodes]); // Run when paused state OR nodes change

  // Listen for agent execution updates
  useWebSocketMessages(
    'agent_execution_update',
    useCallback(
      (data: any) => {
        if (data.workflow_id === workflowId) {
          console.log('[PipelineViz] Agent execution update:', {
            step_id: data.step_id,
            status: data.status,
            processor_type: data.processor_type
          });
          if (data.step_id && data.status) {
            updateNodeStatus(data.step_id, data.status);
          }
        }
      },
      [workflowId, updateNodeStatus]
    )
  );

  // Listen for workflow updates
  useWebSocketMessages(
    'workflow_update',
    useCallback(
      (data: any) => {
        if (data.workflow_id === workflowId) {
          console.log('[PipelineViz] Workflow update:', data);
          
          // When workflow completes/fails/cancels, clear all running statuses
          const terminalStatuses = ['completed', 'failed', 'cancelled', 'COMPLETED', 'FAILED', 'CANCELLED'];
          if (data.status && terminalStatuses.includes(data.status)) {
            console.log('[PipelineViz] Workflow terminal state - clearing running statuses');
            setNodes((nds) =>
              nds.map((node) => {
                // If node is currently running, set to pending
                if (node.data.status === 'in_progress' || 
                    node.data.status === 'running' || 
                    node.data.status === 'calling_sub_agent') {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      status: 'pending',
                    },
                  };
                }
                return node;
              })
            );
          }
        }
      },
      [workflowId, setNodes]
    )
  );

  return (
    <div style={{ width: '100%', height: '400px', border: '1px solid #94a3b8', borderRadius: '8px' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          minZoom: 0.1,
          maxZoom: 1,
        }}
        minZoom={0.1}
        maxZoom={1}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        panOnDrag={false}
        preventScrolling={false}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
      >
      </ReactFlow>
    </div>
  );
};

export default PipelineVisualization;

